/* eslint-disable @typescript-eslint/no-explicit-any */
import { includes } from 'es-toolkit/compat';
import * as cookie from 'cookie';
import { TockCookieName, getTockCookieDetails } from '../constants/browser-storage/TockCookies';
import { getConsentLevelFromDocument } from './cookieConsentUtils';
import Cookies from 'cookies-js';

export function getCookie(cookieName: string) {
  let cookieValue = (Cookies as any).get?.(cookieName);
  if (!cookieValue) {
    return null;
  }
  try {
    cookieValue = JSON.parse(cookieValue);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return null;
  }
  return cookieValue;
}

export function getCookieRaw(cookieName: string) {
  const cookieValue = (Cookies as any).get?.(cookieName);
  if (!cookieValue) {
    return null;
  }
  return cookieValue;
}

export function deleteCookie(cookieName: string) {
  return Cookies.expire(cookieName);
}

/**
 * Add suffixes to base cookie name (e.g., environment suffix)
 */
export const appendCookieSuffixes = (cookieName: string, options: CookieSuffixOptions) => {
  // Add env suffix
  if (options?.envSuffix) {
    cookieName += `-${options.envSuffix}`;
  }
  return cookieName;
};

interface CookieSuffixOptions {
  envSuffix?: string;
}

export interface SetCookieOptions {
  domainOverride?: string;
  expirationFromNow?: number;
  sameSiteNone?: boolean;
  suffixOptions?: CookieSuffixOptions;
}

/**
 * set a cookie client side
 * @param cookieName
 * @param cookieValue
 * @param expirationFromNow
 * @param domainOverride - overrides the domain set for the cookie, otherwise uses hostname
 * @param sameSiteNone
 */
export function setCookie(
  cookieNameRaw: TockCookieName,
  cookieValue: any,
  setCookieOptions?: SetCookieOptions
) {
  // Get cookie information from TockCookies constants
  let { domainOverride, expirationFromNow, sameSiteNone, suffixOptions } =
    getTockCookieDetails(cookieNameRaw);

  // Override cookie information if necessary
  domainOverride = setCookieOptions?.domainOverride ?? domainOverride;
  expirationFromNow = setCookieOptions?.expirationFromNow ?? expirationFromNow;
  sameSiteNone = setCookieOptions?.sameSiteNone ?? sameSiteNone;
  suffixOptions = setCookieOptions?.suffixOptions ?? suffixOptions;

  // Get user's consent level from preference cookie
  const userConsentLevel = getConsentLevelFromDocument(true);

  if (!userConsentLevel.includes(getTockCookieDetails(cookieNameRaw).consentCategory)) {
    return;
  }

  // Append cookie suffixes
  let cookieName: string;
  if (suffixOptions) {
    cookieName = appendCookieSuffixes(cookieNameRaw, suffixOptions);
  } else {
    cookieName = cookieNameRaw;
  }

  // For any cookie we set after this code push, we will set a null 0-TTL cookie using the old domain logic
  // and then the real value with the real TTL to the new domain
  const oldCookieDomain = getOldCookieDomain({ domainOverride, hostname: location.hostname });
  const oldDomainCookieParts = getCookieParts(cookieName, null, oldCookieDomain, 0, sameSiteNone);
  window.document.cookie = cookiePartsToCookie(oldDomainCookieParts);

  const cookieDomain = getCookieDomain({ domainOverride, hostname: location.hostname });
  const cookieParts = getCookieParts(
    cookieName,
    cookieValue,
    cookieDomain,
    expirationFromNow,
    sameSiteNone
  );
  window.document.cookie = cookiePartsToCookie(cookieParts);
}

function getCookieParts(
  cookieName: string,
  cookieValue: any,
  cookieDomain: string,
  expirationFromNow: number | undefined,
  sameSiteNone: boolean | undefined
) {
  let cookieParts = [
    [cookieName, cookieValue ? JSON.stringify(cookieValue) : cookieValue],
    ['path', '/'],
  ];

  // Only set expires with a cookie value
  if (!cookieValue) {
    cookieParts.push(['expires', new Date(new Date().getTime() - 10000).toUTCString()]);
  } else if (expirationFromNow) {
    cookieParts.push(['expires', new Date(new Date().getTime() + expirationFromNow).toUTCString()]);
  }

  if (cookieDomain !== 'localhost') {
    cookieParts = cookieParts.concat([
      ['domain', cookieDomain],
      ['secure', 'true'],
    ]);

    const setSameSite = sameSiteNone === undefined ? true : sameSiteNone;
    if (setSameSite) {
      cookieParts.push(['SameSite', 'None']);
    }
  }
  return cookieParts;
}

function cookiePartsToCookie(cookieParts: any[][]) {
  return cookieParts
    .map((part) => {
      return part.join('=');
    })
    .join('; ');
}

interface GetCookieDomainOptions {
  /**
   * override the domain instead of using hostname
   */
  domainOverride?: string;
  /**
   * current hostname, on client is window.location.hostname
   * on server, is req.hostname
   */
  hostname?: string;
}

export function getCookieDomain({ domainOverride, hostname }: GetCookieDomainOptions) {
  let cookieDomain = 'localhost';
  if (
    hostname &&
    (includes(hostname, '.tocktix.com') || includes(hostname, '.exploretock.com'))
  ) {
    cookieDomain = hostname;
  } else if (includes(hostname, 'dining.chase.com')) {
    cookieDomain = domainOverride || 'dining.chase.com';
  } else if (includes(hostname, 'dininguat.chase.com')) {
    cookieDomain = domainOverride || 'dininguat.chase.com';
  }
  return cookieDomain;
}

export function getOldCookieDomain({ domainOverride, hostname }: GetCookieDomainOptions) {
  let cookieDomain = 'localhost';
  if (includes(hostname, '.tocktix.com')) {
    cookieDomain = '.tocktix.com';
  } else if (includes(hostname, '.exploretock.com')) {
    cookieDomain = '.exploretock.com';
  } else if (includes(hostname, 'dining.chase.com')) {
    cookieDomain = domainOverride || 'dining.chase.com';
  } else if (includes(hostname, 'dininguat.chase.com')) {
    cookieDomain = domainOverride || 'dininguat.chase.com';
  }
  return cookieDomain;
}

export function parseCookies(cookieString: string): { [key: string]: string } {
  return cookie.parse(cookieString);
}
